<template>
  <div class="category-attributes-section" data-testid="so-category-attributes-picker">
    <h4>{{ t('Atributos do Item') }}</h4>
    <p class="description-help-text">{{ t('Marque um ou mais atributos cujos valores serão usados como descrição do item nas listas (concatenados na ordem exibida).') }}</p>
    <div class="steps-list">
      <div
        v-for="(attr, index) in attributes"
        :key="attr._key"
        class="step-row"
        :class="{ 'dragging': dragIndex === index, 'drag-over': dragOverIndex === index }"
        :data-testid="`so-category-attributes-row-${index}`"
        draggable="true"
        @dragstart="onDragStart(index, $event)"
        @dragover.prevent="onDragOver(index)"
        @dragenter.prevent="onDragOver(index)"
        @dragleave="onDragLeave(index)"
        @drop.prevent="onDrop(index)"
        @dragend="onDragEnd"
      >
        <span class="step-drag-handle" :title="t('Arrastar para reordenar')" :data-testid="`so-category-attributes-drag-handle-${index}`">
          <i class="pi pi-bars"></i>
        </span>
        <span class="step-order">{{ index + 1 }}.</span>
        <select-list
          :elem-id="elemIdPrefix + '_' + index"
          v-model="attr.attribute_id"
          :itens-select="attributeOptions"
          :placeholder="t('Selecione um atributo')"
          :auto-select-first="false"
          :data-testid="`so-category-attributes-select-${index}`"
          style="flex: 1;"
        />
        <label class="required-toggle">
          <input
            :data-testid="`${elemIdPrefix}_required_${index}`"
            type="checkbox"
            v-model="attr.required"
          />
          {{ t('Obrigatório') }}
        </label>
        <label class="description-toggle">
          <input
            type="checkbox"
            v-model="attr.is_description"
            :data-testid="`cat-attr-row-${attr.attribute_id}-is-description`"
          />
          {{ t('Descrição') }}
        </label>
        <Button
          icon="pi pi-trash"
          severity="danger"
          text
          rounded
          size="small"
          :data-testid="`so-category-attributes-delete-btn-${index}`"
          @click="removeAttribute(index)"
        />
      </div>
    </div>
    <Button
      :label="t('Adicionar Atributo')"
      icon="pi pi-plus"
      severity="secondary"
      size="small"
      outlined
      data-testid="so-category-attributes-add-btn"
      @click="addAttribute"
      style="margin-top: 8px;"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOItemAttributes } from '../../composables/useSOItemAttributes';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface AttributeOption {
  ID: string;
  Name: string;
}

interface AttributeRow {
  attribute_id: string | null;
  required: boolean;
  is_description: boolean;
  _key: number;
}

interface FetchedAttribute {
  so_item_attribute_id: Id;
  so_item_attribute_name: string;
}

interface CategoryAttributeBinding {
  so_category_attribute_attribute_id: Id;
  so_category_attribute_required: number | string;
  so_category_attribute_is_description?: number | string;
}

interface Props {
  categoryId: Id;
  elemIdPrefix?: string;
}

const props = withDefaults(defineProps<Props>(), {
  elemIdPrefix: 'soCategoryAttr'
});

const { t } = useI18n();
const { fetchAll, fetchCategoryAttributes, saveCategoryAttributes } = useSOItemAttributes();

const attributeOptions = ref<AttributeOption[]>([]);
const attributes = ref<AttributeRow[]>([]);
let keyCounter = 0;

const dragIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);

const onDragStart = (index: number, event: DragEvent): void => {
  dragIndex.value = index;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
  }
};

const onDragOver = (index: number): void => {
  if (dragIndex.value === null || dragIndex.value === index) return;
  dragOverIndex.value = index;
};

const onDragLeave = (index: number): void => {
  if (dragOverIndex.value === index) {
    dragOverIndex.value = null;
  }
};

const onDrop = (index: number): void => {
  if (dragIndex.value === null || dragIndex.value === index) return;
  const item = attributes.value.splice(dragIndex.value, 1)[0];
  attributes.value.splice(index, 0, item);
  log('SOCategoryAttributesPicker', 'onDrop', 'Reordered', { from: dragIndex.value, to: index });
  dragIndex.value = null;
  dragOverIndex.value = null;
};

const onDragEnd = (): void => {
  dragIndex.value = null;
  dragOverIndex.value = null;
};

const loadOptions = async (): Promise<void> => {
  try {
    const fetched = await fetchAll() as FetchedAttribute[];
    attributeOptions.value = fetched.map((item) => ({
      ID: String(item.so_item_attribute_id),
      Name: item.so_item_attribute_name
    }));
  } catch (err: unknown) {
    logError('SOCategoryAttributesPicker', 'loadOptions', 'Error', err);
    attributeOptions.value = [];
  }
  eventBus.emit('so-category-attributes-picker.options-loaded', { count: attributeOptions.value.length });
};

const loadAttributes = async (): Promise<void> => {
  try {
    const bindings = await fetchCategoryAttributes(Number(props.categoryId)) as CategoryAttributeBinding[];
    attributes.value = bindings.map((b) => ({
      attribute_id: String(b.so_category_attribute_attribute_id),
      required: b.so_category_attribute_required === 1 || b.so_category_attribute_required === '1',
      is_description: b.so_category_attribute_is_description === 1 || b.so_category_attribute_is_description === '1',
      _key: ++keyCounter
    }));
  } catch (err: unknown) {
    logError('SOCategoryAttributesPicker', 'loadAttributes', 'Error', err);
    attributes.value = [];
  }
};

const addAttribute = (): void => {
  attributes.value.push({
    attribute_id: null,
    required: false,
    is_description: false,
    _key: ++keyCounter
  });
  eventBus.emit('so-category-attributes-picker.row-added', { count: attributes.value.length });
};

const removeAttribute = (index: number): void => {
  attributes.value.splice(index, 1);
};

const saveAttributes = async (): Promise<void> => {
  const payload = attributes.value
    .filter((a) => a.attribute_id)
    .map((a, i) => ({
      attribute_id: Number(a.attribute_id),
      required: a.required ? 1 : 0,
      display_order: i + 1,
      is_description: a.is_description ? 1 : 0
    }));

  await saveCategoryAttributes(props.categoryId, payload);
};

onMounted(async () => {
  log('SOCategoryAttributesPicker', 'onMounted', 'Loading', { categoryId: props.categoryId });
  await loadOptions();
  await loadAttributes();
});

defineExpose({ saveAttributes, loadAttributes });
</script>

<style scoped>
.category-attributes-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-surface-200);
}

.category-attributes-section h4 {
  margin-bottom: 12px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.step-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 2px solid transparent;
  background: var(--p-surface-0);
  transition: border-color 0.15s, background 0.15s, opacity 0.15s;
}

.step-row.dragging {
  opacity: 0.4;
}

.step-row.drag-over {
  border-color: var(--p-primary-color);
  background: var(--p-primary-50);
}

.step-drag-handle {
  cursor: grab;
  color: var(--p-text-muted-color);
  padding: 4px;
  display: flex;
  align-items: center;
}

.step-drag-handle:active {
  cursor: grabbing;
}

.step-order {
  font-weight: 600;
  min-width: 24px;
  color: var(--p-text-muted-color);
}

.required-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
  cursor: pointer;
}

.description-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  white-space: nowrap;
  cursor: pointer;
}

.description-help-text {
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  margin-bottom: 8px;
}
</style>
